import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  FormGroup,
  Label,
  TextArea,
  Grid,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { BudgetCategoryType } from '../../types';
import NotificationService from '../../services/NotificationService';
import apiService from '../../services/api';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const StatusBadge = styled(Badge)`
  min-width: 80px;
  text-align: center;
`;

const AmountText = styled.span`
  font-weight: 500;
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: var(--border-radius);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--light-gray);
  padding-bottom: 1rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--dark-gray);
  
  &:hover {
    color: var(--danger-color);
  }
`;



const StoreKeeperExpenseTracking: React.FC = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<any[]>([]);
  const [filteredExpenses, setFilteredExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: 0,
    date: '',
    supplier: '',
    receiptNumber: '',
    receiptType: 'TRA VAT'
  });

  // Load expenses data
  useEffect(() => {
    const loadExpensesData = async () => {
      try {
        setLoading(true);
        const expensesResponse = await apiService.getExpenses();
        setExpenses(expensesResponse.data || []);
      } catch (error) {
        console.error('Error loading expenses:', error);
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadExpensesData();
    }
  }, [user]);

  // Filter expenses
  useEffect(() => {
    let filtered = expenses;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(exp => 
        (exp._id || exp.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        exp.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exp.supplier.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(exp => exp.category === categoryFilter);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(exp => exp.status === statusFilter);
    }
    
    setFilteredExpenses(filtered);
    setCurrentPage(1);
  }, [expenses, searchTerm, categoryFilter, statusFilter]);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Recorded':
        return 'success';
      case 'Pending Approval':
        return 'warning';
      case 'Rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-TZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const expenseData = {
        projectId: 'office', // For office expenses
        category: formData.category,
        description: formData.description,
        amount: parseFloat(formData.amount.toString()),
        date: formData.date,
        supplier: formData.supplier,
        receiptNumber: formData.receiptNumber,
        receiptType: formData.receiptType
      };

      const response = await apiService.createExpense(expenseData);
      
      if (response.success) {
        // Refresh expenses list
        const expensesResponse = await apiService.getExpenses();
        setExpenses(expensesResponse.data || []);
        
        setIsModalOpen(false);
        setFormData({
          category: '',
          description: '',
          amount: 0,
          date: '',
          supplier: '',
          receiptNumber: '',
          receiptType: 'TRA VAT'
        });
        
        console.log('New expense recorded:', response.data);
      }
    } catch (error) {
      console.error('Error recording expense:', error);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredExpenses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '1.1rem',
        color: '#6c757d'
      }}>
        Loading expenses...
      </div>
    );
  }

  return (
    <div>
      <PageHeader>
        <h1>Expense Tracking</h1>
        <ActionButtons>
          <Button onClick={openModal}>
            Record New Expense
          </Button>
          <OutlineButton as={Link} to="/storekeeper/dashboard">
            Back to Dashboard
          </OutlineButton>
        </ActionButtons>
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <Input
            type="text"
            placeholder="Search expenses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        
        <FilterGroup>
          <label>Category:</label>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="Rent">Rent</option>
            <option value="Stationary">Stationary</option>
            <option value="Salary">Salary</option>
            <option value="CRDB/ERB Fees">CRDB/ERB Fees</option>
            <option value="POSTA">POSTA</option>
            <option value="Electricity">Electricity</option>
            <option value="Communication">Communication</option>
            <option value="Emergency">Emergency</option>
            <option value="Parking">Parking</option>
            <option value="Tendering Fees">Tendering Fees</option>
            <option value="Audit Fees">Audit Fees</option>
            <option value="Others">Others</option>
          </Select>
        </FilterGroup>
        
        <FilterGroup>
          <label>Status:</label>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="Recorded">Recorded</option>
            <option value="Pending Approval">Pending Approval</option>
            <option value="Rejected">Rejected</option>
          </Select>
        </FilterGroup>
      </FilterSection>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Expense ID</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader>Description</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader>Supplier</TableHeader>
              <TableHeader>Receipt</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <tbody>
            {currentItems.map((expense) => (
              <TableRow key={expense._id || expense.id}>
                <TableCell>
                  <strong>{expense._id || expense.id}</strong>
                </TableCell>
                <TableCell>
                  <Badge variant="info">{expense.category}</Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <strong>{expense.description}</strong>
                    <br />
                    <small>Recorded by {expense.recordedBy}</small>
                  </div>
                </TableCell>
                <TableCell>
                  <AmountText>{formatAmount(expense.amount, expense.currency)}</AmountText>
                </TableCell>
                <TableCell>
                  <DateText>{formatDate(expense.date)}</DateText>
                </TableCell>
                <TableCell>
                  {expense.supplier}
                </TableCell>
                <TableCell>
                  <div>
                    <strong>{expense.receiptNumber}</strong>
                    <br />
                    <small>{expense.receiptType}</small>
                  </div>
                </TableCell>
                <TableCell>
                  <StatusBadge variant={getStatusVariant(expense.status)}>
                    {expense.status}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <ActionButtons>
                    <Button
                      as={Link}
                      to={`/storekeeper/expense/${expense._id || expense.id}`}
                    >
                      View
                    </Button>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Pagination>
          <PageButton
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </PageButton>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
            <PageButton
              key={number}
              onClick={() => paginate(number)}
              active={currentPage === number}
            >
              {number}
            </PageButton>
          ))}
          
          <PageButton
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </PageButton>
        </Pagination>
      )}

      {currentItems.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>No expenses found matching your criteria.</p>
        </div>
      )}

      {/* Add Expense Modal */}
      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <h2>Record New Expense</h2>
            <CloseButton onClick={closeModal}>&times;</CloseButton>
          </ModalHeader>
          
          <form onSubmit={handleSubmit}>
            <Grid columns="1fr 1fr" gap="1rem">
              <FormGroup>
                <Label>Category *</Label>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="Rent">Rent</option>
                  <option value="Stationary">Stationary</option>
                  <option value="Salary">Salary</option>
                  <option value="CRDB/ERB Fees">CRDB/ERB Fees</option>
                  <option value="POSTA">POSTA</option>
                  <option value="Electricity">Electricity</option>
                  <option value="Communication">Communication</option>
                  <option value="Emergency">Emergency</option>
                  <option value="Parking">Parking</option>
                  <option value="Tendering Fees">Tendering Fees</option>
                  <option value="Audit Fees">Audit Fees</option>
                  <option value="Others">Others</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Amount (TZS) *</Label>
                <Input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="1000"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Date *</Label>
                <Input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Supplier *</Label>
                <Input
                  type="text"
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Receipt Number *</Label>
                <Input
                  type="text"
                  name="receiptNumber"
                  value={formData.receiptNumber}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Receipt Type *</Label>
                <Select
                  name="receiptType"
                  value={formData.receiptType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Tax Invoice">Tax Invoice</option>
                  <option value="Invoice">Invoice</option>
                </Select>
              </FormGroup>
            </Grid>
            
            <FormGroup>
              <Label>Description *</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={3}
                placeholder="Describe the expense..."
              />
            </FormGroup>
            
            <FlexContainer gap="0.5rem" style={{ justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <OutlineButton type="button" onClick={closeModal}>
                Cancel
              </OutlineButton>
              <Button type="submit">
                Record Expense
              </Button>
            </FlexContainer>
          </form>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default StoreKeeperExpenseTracking; 